import { observer } from "mobx-react-lite";
import * as S from "../style";
import { Button, Icon } from "@smartsuite/react-ui/lib";
import type {
  ActionTest,
  GetActionTestResponse,
} from "@smartsuite/automation-engine-api";
import { translateIfPresent } from "../../../../../../locales/i18n";
import { StatusBadge } from "../elements/status-badge";
import { type ReactElement, useState } from "react";
import { ArrowUpDown } from "../../../../../../../generated/images";
import { useTranslation } from "react-i18next";
import { useResponseDetail } from "../hooks/response-detail.hook";

export const ResponseDetails = observer<{
  actionTestResponse: GetActionTestResponse;
  onClickTest: (path?: string) => void;
}>(({ actionTestResponse, onClickTest }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { outputs, inputData } = useResponseDetail(actionTestResponse);

  const renderStatus = (actionTest?: ActionTest): ReactElement => {
    if (actionTest === undefined) {
      return <StatusBadge status={"error"} title={"No response"} />;
    } else if (actionTest.status.oneofKind === "complete") {
      return (
        <StatusBadge
          status={"success"}
          title={translateIfPresent(
            "automation.action-test-success",
            "Automation completed successfully",
          )}
          subTitle={actionTest.status.complete.message}
        />
      );
    } else if (actionTest.status.oneofKind === "error") {
      return (
        <StatusBadge
          status={"error"}
          title={translateIfPresent(
            "automation.action-test-error",
            "Failed to complete automation",
          )}
          subTitle={actionTest.status.error.message}
        />
      );
    }
    return <></>;
  };

  const onExpand = (): void => {
    setExpanded(!expanded);
  };

  const onClickCopy = (): void => {
    void navigator.clipboard.writeText(JSON.stringify(inputData));
  };

  return (
    <S.Wrapper>
      {renderStatus(actionTestResponse?.actionTest)}
      <S.InputData>
        <S.InputDataTitle>
          {translateIfPresent("general.inputs", "Inputs")}
          <S.IconCopyWrapper onClick={onClickCopy}>
            <Icon name={"copy-lined"} size={"S"} />
          </S.IconCopyWrapper>
        </S.InputDataTitle>
        <S.InputDataItemWrapper expanded={expanded}>
          {inputData.map((data) => (
            <S.InputDataItem key={data.id}>
              <S.InputDataItemLabel>{data.label}</S.InputDataItemLabel>
              <S.InputDataItemValue>{data.value}</S.InputDataItemValue>
            </S.InputDataItem>
          ))}
        </S.InputDataItemWrapper>
        <S.InputDataExpand onClick={onExpand}>
          <S.IconWrapper top={"unset"}>
            <ArrowUpDown />
          </S.IconWrapper>
          {translateIfPresent("general.expand", "Expand")}
        </S.InputDataExpand>
      </S.InputData>
      <S.OutputData>
        <S.OutputDataTitle>
          {translateIfPresent(
            "automation.action-test-available-fields",
            "Available fields",
          )}
        </S.OutputDataTitle>
        <S.OutputDataItemWrapper>
          {outputs.map((output) => (
            <S.OutputDataItem key={output.outputId}>
              {output.icon && (
                <S.IconWrapper top={"3px"}>
                  <Icon size={"S"} name={output.icon} />
                </S.IconWrapper>
              )}
              <S.OutputDataItemLabel>
                {output.outputLabel}
              </S.OutputDataItemLabel>
              <S.OutputDataItemValue
                noValue={output.value === t("general.no-value")}
              >
                {output.value}
              </S.OutputDataItemValue>
            </S.OutputDataItem>
          ))}
        </S.OutputDataItemWrapper>
      </S.OutputData>
      <S.Hr />
      <S.ButtonWrapper>
        <Button onClick={() => onClickTest("test")}>
          {translateIfPresent("automation.action-test-again", "Test Again")}
        </Button>
      </S.ButtonWrapper>
    </S.Wrapper>
  );
});
